import React, { useEffect, useState } from 'react';
import {
  ChevronRight, ArrowRight, ArrowUpRight, SquareArrowOutUpRight, TrendingUp
} from 'lucide-react';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IndicesGraph from './graph';
import MyEditDialog from './editDialog';
import TopStocksDialog from './topStocksDialog';
import EarningsDialog from './earningsDialog';
import TopYieldingStocksDialog from './topYieldingStocksDialog';
import { getNews } from '../../../api/api';
import { Skeleton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { getTrendingStocks, getMarketMovers } from '../../../api/api';
import { getCurrencySymbol, fmtL } from "../../../utils/currency";

const sym = getCurrencySymbol();

const getTimeAgo = (publishedAt) => {
  const now = Date.now();
  const published = new Date(publishedAt).getTime();
  const diffMinutes = Math.floor((now - published) / (1000 * 60));
  if (diffMinutes < 1) return "Now";
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${Math.floor(diffHours / 24)}d ago`;
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [myFinlyticOpen, setMyFinlyticOpen] = useState(false);
  const [topStocksOpen, setTopStocksOpen] = useState(false);
  const [earningsDialogOpen, setEarningsDialogOpen] = useState(false);
  const [topYieldingOpen, setTopYieldingOpen] = useState(false);
  const [latestNews, setLatestNews] = useState([]);
  const [trendingStocks, setTrendingStocks] = useState([]);
  const [loadingNews, setLoadingNews] = useState(true);
  const [newsError, setNewsError] = useState(false);
  const [loadingTrendingNews, setLoadingTrendingNews] = useState(true);
  const [marketMovers, setMarketMovers] = useState({ actives: [], gainers: [], losers: [] });
  const [loadingMarketMovers, setLoadingMarketMovers] = useState(true);
  const [saving, setSaving] = useState(false);

  const transformMarketMoverData = (stocks = []) =>
    stocks.map((stock) => {
      const changePct = typeof stock?.change_pct === "number" ? stock.change_pct : 0;
      return {
        symbol: stock?.symbol ?? "—",
        company: stock?.name ?? "—",
        change: `${changePct >= 0 ? "+" : ""}${changePct.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`,
        isPositive: changePct >= 0,
        price: stock?.price ?? 0,
        changeAmount: typeof stock?.change === "number" ? stock.change : 0,
      };
    });

  useEffect(() => {
    const fetch = async () => {
      setLoadingMarketMovers(true);
      const res = await getMarketMovers();
      if (res?.success) {
        setMarketMovers({
          actives: transformMarketMoverData(res.data?.actives || []),
          gainers: transformMarketMoverData(res.data?.gainers || []),
          losers: transformMarketMoverData(res.data?.losers || []),
        });
      }
      setLoadingMarketMovers(false);
    };
    fetch();
  }, []);

  useEffect(() => {
    const fetch = async () => {
      setLoadingNews(true);
      setNewsError(false);
      const res = await getNews(undefined, 1, 3);
      if (res?.success) {
        setLatestNews(res.data?.items || []);
      } else {
        setNewsError(true);
      }
      setLoadingNews(false);
    };
    fetch();
  }, []);

  useEffect(() => {
    const fetch = async () => {
      setLoadingTrendingNews(true);
      const res = await getTrendingStocks();
      if (res?.success) setTrendingStocks(res.data?.response_data || []);
      setLoadingTrendingNews(false);
    };
    fetch();
  }, []);

  const [quickAccessConfig, setQuickAccessConfig] = useState([
    { id: "earnings", label: "Earnings Calendar", visible: true, onClick: () => setEarningsDialogOpen(true) },
    { id: "top-yield", label: "Top Yielding Stocks", visible: true, onClick: () => setTopYieldingOpen(true) },
    { id: "quant-sector", label: "Latest FA Ratings", visible: true, onClick: () => setTopStocksOpen(true) },
  ]);

  const visibleQuickAccessItems = quickAccessConfig.filter(item => item.visible);

  /* ── HoverCard ── */
  const HoverCard = ({ children, style = {} }) => {
    const [isHovered, setIsHovered] = useState(false);
    return (
      <div
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          backdropFilter: "blur(15px)",
          boxShadow: isHovered ? "0 20px 40px rgba(0,0,0,0.08)" : "0 8px 32px rgba(0,0,0,0.04)",
          transition: "all 0.3s ease",
          transform: isHovered ? "translateY(-4px)" : "translateY(0)",
          position: "relative",
          ...style,
        }}
      >
        {children}
      </div>
    );
  };

  /* ── Mini Stock Graph ── */
  const MiniStockGraph = () => {
    const dataPoints = [45, 52, 48, 55, 58, 54, 60, 65, 62, 68, 72, 70, 75, 78, 82, 85, 88, 92, 95, 98];
    const width = 200, height = 150, padding = 4;
    const max = Math.max(...dataPoints), min = Math.min(...dataPoints);
    const range = max - min;
    const points = dataPoints.map((v, i) => {
      const x = padding + (i * (width - padding * 2)) / (dataPoints.length - 1);
      const y = height - padding - ((v - min) / range) * (height - padding * 2);
      return `${x},${y}`;
    }).join(' ');

    return (
      <svg
        width={width}
        height={height}
        className="mini-stock-graph"
        style={{ position: 'absolute', bottom: '16px', right: '16px', opacity: 0.6, transition: 'opacity var(--transition-normal)' }}
      >
        <defs>
          <linearGradient id="graphGradient" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="var(--color-chart-positive)" stopOpacity="0.3" />
            <stop offset="100%" stopColor="var(--color-chart-positive)" stopOpacity="0" />
          </linearGradient>
        </defs>
        <line x1={padding} y1={height / 2} x2={width - padding} y2={height / 2} stroke="var(--color-chart-grid)" strokeWidth="0.5" opacity="0.3" />
        <polygon points={`${padding},${height - padding} ${points} ${width - padding},${height - padding}`} fill="url(#graphGradient)" />
        <polyline points={points} fill="none" stroke="var(--color-chart-positive)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx={width - padding} cy={height - padding - ((dataPoints[dataPoints.length - 1] - min) / range) * (height - padding * 2)} r="3" fill="var(--color-chart-positive)" />
      </svg>
    );
  };

  /* ── AI Alpha Insight ── */
  const AIAlphaInsight = () => {
    const [isHovered, setIsHovered] = useState(false);
    const [topStocksOpen, setTopStocksOpen] = useState(false);

    return (
      <div
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          position: 'relative',
          display: "flex",
          flexDirection: "column",
          gap: "var(--spacing-lg)",
          backgroundColor: "var(--color-surface)",
          border: "1px solid var(--color-border-primary)",
          borderRadius: "var(--radius-xl)",
          padding: "var(--spacing-xl)",
          transition: "all var(--transition-normal)",
          transform: isHovered ? "translateY(-4px)" : "translateY(0)",
          boxShadow: isHovered ? "var(--shadow-xl)" : "var(--shadow-md)",
        }}
      >
        <MiniStockGraph />

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "var(--spacing-sm)", flexWrap: "wrap" }}>
          <Typography variant="title">
            Top 10 High-Conviction Stocks
          </Typography>
          <span
            style={{
              backgroundColor: "var(--color-primary)",
              color: "var(--color-text-inverse)",
              fontSize: "var(--font-size-sm)",
              fontWeight: "var(--font-weight-bold)",
              padding: "4px 12px",
              borderRadius: "var(--radius-md)",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            FactorAlpha Insight
          </span>
        </div>

        {/* Description */}
        <Typography
          variant="paragraph"
          className="ai-insight-description"
          sx={{ color: "var(--color-text-secondary)", lineHeight: 1.7 }}
        >
          Our research team has identified 10 high-conviction opportunities positioned to outperform in 2026.
          Each stock is selected using a disciplined framework combining{" "}
          <strong>valuation, growth, momentum, profitability strength, and risk factors</strong>.
        </Typography>

        <Button
          variant="contained"
          onClick={() => setTopStocksOpen(true)}
          sx={{
            alignSelf: "flex-start", display: "inline-flex", alignItems: "center", gap: "var(--spacing-sm)",
            justifyContent: "space-between",
            fontSize: "var(--font-size-base)",
            fontWeight: "var(--font-weight-semibold)",
          }}
        >
          Top Stocks
          <SquareArrowOutUpRight size={16} />
        </Button>

        <TopStocksDialog open={topStocksOpen} onClose={() => setTopStocksOpen(false)} />
      </div>
    );
  };

  /* ── Latest Market News ── */
  const LatestMarketNews = () => {
    const [isHovered, setIsHovered] = useState(false);
    const getTimeAgoLocal = (timestamp) => {
      // Append 'Z' so it's always parsed as UTC, not local time
      const published = new Date(timestamp.endsWith('Z') ? timestamp : timestamp + 'Z');
      const diffMins = Math.floor((new Date() - published) / 60000);
      if (diffMins < 1) return "Just now";
      if (diffMins < 60) return `${diffMins}m ago`;
      const diffHours = Math.floor(diffMins / 60);
      if (diffHours < 24) return `${diffHours}h ago`;
      return `${Math.floor(diffHours / 24)}d ago`;
    };

    return (
      <div
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          backgroundColor: "var(--color-surface)",
          border: "1px solid var(--color-border-primary)",
          borderRadius: "var(--radius-xl)",
          padding: "var(--spacing-lg)",
          color: "var(--color-text-primary)",
          transition: "all var(--transition-normal)",
          transform: isHovered ? "translateY(-4px)" : "translateY(0)",
          boxShadow: isHovered ? "var(--shadow-xl)" : "var(--shadow-md)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "var(--spacing-lg)" }}>
          <Typography variant="title">Latest Market News</Typography>
          <Button variant="contained" onClick={() => navigate("/news")}
            sx={{
              minWidth: "30px",
              padding: "6px",
              flexShrink: 0,
              justifyContent: "space-between",
              fontSize: "var(--font-size-base)",
              fontWeight: "var(--font-weight-semibold)",
            }}>
            <ArrowUpRight size={18} />
          </Button>
        </div>

        {loadingNews ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--spacing-md)" }}>
            {Array.from({ length: 3 }).map((_, idx) => (
              <div key={idx} style={{ display: "flex", gap: "var(--spacing-md)", padding: "var(--spacing-md)", backgroundColor: "var(--color-bg-secondary)", borderRadius: "var(--radius-lg)", border: "1px solid var(--color-border-primary)" }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", gap: "var(--spacing-sm)", marginBottom: "var(--spacing-xs)" }}>
                    <Skeleton variant="text" width={60} height={14} sx={{ bgcolor: "var(--color-bg-tertiary)" }} />
                    <Skeleton variant="text" width={80} height={14} sx={{ bgcolor: "var(--color-bg-tertiary)" }} />
                  </div>
                  <Skeleton variant="text" width="95%" height={18} sx={{ bgcolor: "var(--color-bg-tertiary)" }} />
                  <Skeleton variant="text" width="80%" height={18} sx={{ bgcolor: "var(--color-bg-tertiary)" }} />
                </div>
              </div>
            ))}
          </div>
        ) : latestNews.length === 0 ? (
          <Box sx={{ py: "var(--spacing-2xl)", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center" }}>
            <TrendingUp
              size={40}
              color="var(--color-border-secondary)"
              style={{ marginBottom: "var(--spacing-md)" }}
            />
            <Typography variant="subtitle" sx={{ color: "var(--color-text-primary)", mb: "var(--spacing-sm)" }}>
              {newsError ? "News temporarily unavailable" : "No news found"}
            </Typography>
            <Typography variant="paragraph" sx={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>
              {newsError
                ? "We're having trouble loading news right now. Please try again later."
                : "Check back later for the latest market news."}
            </Typography>
          </Box>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--spacing-md)" }}>
            {latestNews.map((item, idx) => (
              <NewsItem key={item.id || idx} item={item} getTimeAgo={getTimeAgoLocal} index={idx} />
            ))}
          </div>
        )}
      </div>
    );
  };

  /* ── News Item ── */
  const NewsItem = ({ item, getTimeAgo, index = 0 }) => {
    const [isHovered, setIsHovered] = useState(false);
    return (
      <div className="news-border">
        <div className="news-inner" style={{ animationDelay: `${index * 70}ms` }}>
          <div
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={() => window.open(item.url, "_blank", "noopener,noreferrer")}
            style={{
              display: "flex",
              gap: "var(--spacing-md)",
              padding: "var(--spacing-sm)",
              backgroundColor: isHovered ? "var(--color-surface-hover)" : "var(--color-surface)",
              border: "1px solid var(--color-border-primary)",
              borderRadius: "var(--radius-lg)",
              cursor: "pointer",
              transition: "all var(--transition-normal)",
              transform: isHovered ? "translateX(4px)" : "translateX(0)",
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: "var(--spacing-sm)", marginBottom: "var(--spacing-xs)", flexWrap: "wrap" }}>
                <Typography variant="paragraph" sx={{ fontSize: "var(--font-size-sm)", color: "var(--color-text-muted)", fontWeight: "var(--font-weight-medium)" }}>
                  {getTimeAgo(item.created_at)}
                </Typography>
              </div>
              <Typography
                variant="paragraph"
                sx={{ color: "var(--color-text-primary)", fontWeight: "var(--font-weight-medium)", lineHeight: 1.5, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", textOverflow: "ellipsis" }}
                title={item.title}
              >
                {item.title}
              </Typography>
            </div>
            <div style={{ display: "flex", alignItems: "center", opacity: isHovered ? 1 : 0, transform: isHovered ? "translateX(2px)" : "translateX(0)", transition: "all var(--transition-fast)", flexShrink: 0 }}>
              <ArrowRight size={18} color="var(--color-text-muted)" />
            </div>
          </div>
        </div>
      </div>
    );
  };

  /* ── Stock Table ── */
  const StockTable = ({ title, data = [], subtitle, isLoading }) => {
    const truncateCompany = (name) => !name ? "" : name.length > 12 ? name.slice(0, 12) + "..." : name;

    return (
      <div style={{ marginBottom: "var(--spacing-lg)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: "var(--spacing-sm)", marginBottom: "var(--spacing-sm)", borderBottom: "1px solid var(--color-border-primary)" }}>
          <Typography variant="tableHeader" sx={{ color: "var(--color-text-primary)" }}>{title}</Typography>
          <Typography variant="tableHeader" sx={{ color: "var(--color-text-muted)" }}>{subtitle}</Typography>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "var(--spacing-sm)" }}>
          {isLoading ? (
            Array.from({ length: 5 }).map((_, idx) => (
              <div key={idx} style={{ display: "flex", alignItems: "center", padding: "var(--spacing-sm) 0", borderBottom: "1px solid var(--color-border-primary)" }}>
                <Skeleton variant="text" width={70} height={20} sx={{ bgcolor: "var(--color-bg-tertiary)" }} />
                <Skeleton variant="text" width={120} height={20} sx={{ bgcolor: "var(--color-bg-tertiary)", marginLeft: "var(--spacing-sm)" }} />
                <Skeleton variant="text" width={70} height={20} sx={{ bgcolor: "var(--color-bg-tertiary)", marginLeft: "auto" }} />
              </div>
            ))
          ) : data.length === 0 ? (
            <Typography variant="paragraph" sx={{ textAlign: "center", padding: "var(--spacing-md) 0", color: "var(--color-text-secondary)" }}>
              No data available
            </Typography>
          ) : (
            data.map((stock, idx) => (
              <div key={idx} style={{ display: "flex", alignItems: "center", padding: "var(--spacing-sm) 0", borderBottom: idx !== data.length - 1 ? "1px solid var(--color-border-primary)" : "none" }}>
                <span
                  style={{ width: "70px", flexShrink: 0, color: "var(--color-primary)", fontWeight: "var(--font-weight-semibold)", fontSize: "var(--font-size-sm)", cursor: "pointer", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                  onClick={() => navigate(`/stocks/${stock.symbol}/details/summary`)}
                  title={stock.symbol}
                >
                  {stock.symbol}
                </span>
                <span style={{ flex: 1, minWidth: 0, marginLeft: "var(--spacing-sm)", color: "var(--color-text-secondary)", fontSize: "var(--font-size-sm)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={stock.company}>
                  {truncateCompany(stock.company)}
                </span>
                <span style={{ flexShrink: 0, marginLeft: "var(--spacing-sm)", textAlign: "right", fontWeight: "var(--font-weight-semibold)", fontSize: "var(--font-size-sm)", color: stock.isPositive ? "var(--color-success)" : "var(--color-error)" }}>
                  {stock.isPositive ? "▲" : "▼"} {stock.change}
                </span>
              </div>
            ))
          )}
        </div>
      </div>
    );
  };

  /* ── Trending Table ── */
  const trendingColumns = [
    { key: 'ticker', label: 'Ticker', width: null, flex: 1, align: 'left' },
    { key: 'grade', label: 'FA Grade', width: 70, flex: 0, align: 'center' },
    { key: 'score', label: 'FA Score', width: 75, flex: 0, align: 'center' },
    { key: 'price', label: 'Price', width: 85, flex: 0, align: 'right' },
    { key: 'change', label: 'Change', width: 85, flex: 0, align: 'right' },
    { key: 'change_pct', label: 'Change %', width: 90, flex: 0, align: 'right' },
    { key: 'volume', label: 'Volume', width: 90, flex: 0, align: 'right' },
    { key: 'market_cap', label: 'Market Cap', width: 100, flex: 0, align: 'right' },
  ];

  const TrendingTable = ({ title, data = [] }) => {
    const getGradeColor = (grade) => {
      const map = {
        "A+": "var(--color-grade-a-plus)",
        "A": "var(--color-grade-a)",
        "A-": "var(--color-grade-a-minus)",

        "B+": "var(--color-grade-b-plus)",
        "B": "var(--color-grade-b)",
        "B-": "var(--color-grade-b-minus)",

        "C+": "var(--color-grade-c-plus)",
        "C": "var(--color-grade-c)",
        "C-": "var(--color-grade-c-minus)",

        "D+": "var(--color-grade-d-plus)",
        "D": "var(--color-grade-d)",
        "D-": "var(--color-grade-d-minus)",

        "E": "var(--color-grade-e)",

        "F": "var(--color-grade-f)",
      };

      return map[grade] ?? "var(--color-grade-unknown)";
    };

    const fmtLocale = (n, d = 2) => n.toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });

    const formatChange = (val) => {
      if (val === null || val === undefined) return "—";
      const num = parseFloat(val);
      return `${num >= 0 ? "+" : ""}${fmtLocale(num)}`;
    };

    const formatChangePct = (val) => {
      if (val === null || val === undefined) return "—";
      const num = parseFloat(val);
      return `${num >= 0 ? "+" : ""}${fmtLocale(num)}%`;
    };

    const formatVolume = (val) => {
      if (!val) return "—";
      const num = typeof val === 'number' ? val : parseFloat(val);
      if (isNaN(num)) return val;
      if (num >= 1e9) return `${fmtLocale(num / 1e9)}B`;
      if (num >= 1e6) return `${fmtLocale(num / 1e6)}M`;
      if (num >= 1e3) return `${fmtLocale(num / 1e3, 1)}K`;
      return num.toLocaleString();
    };

    const formatMarketCap = (val) => {
      if (!val) return "—";
      const num = typeof val === 'number' ? val : parseFloat(val);
      if (isNaN(num)) return val;
      if (num >= 1e12) return `${sym}${fmtLocale(num / 1e12)}T`;
      if (num >= 1e9) return `${sym}${fmtLocale(num / 1e9)}B`;
      if (num >= 1e6) return `${sym}${fmtLocale(num / 1e6, 1)}M`;
      return `${sym}${num.toLocaleString()}`;
    };

    const formatPrice = (val) => {
      if (!val) return "—";
      const num = typeof val === 'number' ? val : parseFloat(val);
      if (isNaN(num)) return val;
      return `${sym}${fmtLocale(num)}`;
    };

    const renderCell = (stock, col) => {
      const isNeg = parseFloat(stock.change_pct) < 0;
      const changeColor = isNeg ? 'var(--color-error)' : 'var(--color-success)';

      switch (col.key) {
        case 'ticker':
          return (
            <span
              style={{
                color: 'var(--color-primary)',
                fontWeight: 'var(--font-weight-semibold)',
                fontSize: 'var(--font-size-sm)',
                cursor: 'pointer',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'block',
              }}
              onClick={() => navigate(`/stocks/${stock.ticker}/details/summary`)}
            >
              {stock.ticker} {stock.company_name}
            </span>
          );

        case 'grade':
          return (
            <span style={{
              backgroundColor: getGradeColor(stock.grade),
              color: 'var(--color-text-inverse)',
              fontWeight: 'var(--font-weight-semibold)',
              fontSize: 'var(--font-size-sm)',
              padding: '3px 10px',
              borderRadius: 'var(--radius-md)',
              minWidth: 40,
              textAlign: 'center',
              display: 'inline-block',
            }}>
              {stock.grade ?? '—'}
            </span>
          );

        case 'score':
          return (
            <span style={{
              backgroundColor: getGradeColor(stock.grade),
              color: 'var(--color-text-inverse)',
              fontWeight: 'var(--font-weight-semibold)',
              fontSize: 'var(--font-size-sm)',
              padding: '3px 10px',
              borderRadius: 'var(--radius-md)',
              minWidth: 40,
              textAlign: 'center',
              display: 'inline-block',
            }}>
              {stock.score != null ? fmtL(stock.score, 1) : '—'}
            </span>
          );

        case 'price':
          return (
            <span style={{ fontWeight: 'var(--font-weight-semibold)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)' }}>
              {formatPrice(stock.price)}
            </span>
          );

        case 'change':
          return (
            <span style={{ fontWeight: 'var(--font-weight-semibold)', fontSize: 'var(--font-size-sm)', color: changeColor }}>
              {formatChange(stock.change)}
            </span>
          );

        case 'change_pct':
          return (
            <span style={{ fontWeight: 'var(--font-weight-semibold)', fontSize: 'var(--font-size-sm)', color: changeColor }}>
              {formatChangePct(stock.change_pct)}
            </span>
          );

        case 'volume':
          return (
            <span style={{ fontWeight: 'var(--font-weight-medium)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
              {formatVolume(stock.volume)}
            </span>
          );

        case 'market_cap':
          return (
            <span style={{ fontWeight: 'var(--font-weight-medium)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
              {formatMarketCap(stock.market_cap)}
            </span>
          );

        default:
          return '—';
      }
    };

    return (
      <div style={{ padding: 'var(--spacing-lg)', overflowX: 'auto' }}>
        {/* Header Row */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--spacing-sm)',
          paddingBottom: 'var(--spacing-sm)',
          borderBottom: '1px solid var(--color-border-primary)',
          minWidth: 700,
        }}>
          {trendingColumns.map((col) => (
            <div
              key={col.key}
              style={{
                ...(col.width ? { width: col.width, flexShrink: 0 } : { flex: 1, minWidth: 0 }),
                textAlign: col.align,
              }}
            >
              <Typography
                variant="tableHeader"
                sx={{ color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}
              >
                {col.label}
              </Typography>
            </div>
          ))}
        </div>

        {/* Data Rows */}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {data.length === 0 ? (
            <Typography
              variant="paragraph"
              sx={{ textAlign: 'center', padding: 'var(--spacing-lg) 0', color: 'var(--color-text-secondary)' }}
            >
              No data available
            </Typography>
          ) : (
            data.map((stock, idx) => (
              <div
                key={stock.ticker || idx}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--spacing-sm)',
                  padding: 'var(--spacing-sm) 0',
                  borderBottom: idx !== data.length - 1 ? '1px solid var(--color-border-primary)' : 'none',
                  minWidth: 700,
                  transition: 'background-color var(--transition-fast)',
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-surface-hover)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                {trendingColumns.map((col) => (
                  <div
                    key={col.key}
                    style={{
                      ...(col.width ? { width: col.width, flexShrink: 0 } : { flex: 1, minWidth: 0 }),
                      textAlign: col.align,
                      display: 'flex',
                      justifyContent: col.align === 'center' ? 'center' : col.align === 'right' ? 'flex-end' : 'flex-start',
                      alignItems: 'center',
                    }}
                  >
                    {renderCell(stock, col)}
                  </div>
                ))}
              </div>
            ))
          )}
        </div>
      </div>
    );
  };

  return (
    <Box sx={{ backgroundColor: "var(--color-bg-primary)", minHeight: "100vh", pb: "var(--spacing-2xl)" }}>
      <IndicesGraph />

      {/* ── Top Section ── */}
      <Box sx={{ px: { xs: "var(--spacing-md)", sm: "var(--spacing-lg)", md: "var(--spacing-xl)" }, pt: "var(--spacing-xl)" }}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "repeat(2, 1fr)" }, gap: "var(--spacing-xl)" }}>
          <AIAlphaInsight />
          <LatestMarketNews />
        </Box>
      </Box>

      {/* ── Main Content ── */}
      <Box sx={{ px: { xs: "var(--spacing-md)", sm: "var(--spacing-lg)", md: "var(--spacing-xl)" }, mt: "var(--spacing-2xl)" }}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "3fr 1fr" }, gap: "var(--spacing-xl)" }}>

          {/* LEFT */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: "var(--spacing-xl)" }}>

            {/* Gainers & Losers */}
            <HoverCard>
              <Box sx={{ backgroundColor: "var(--color-surface)", border: "1px solid var(--color-border-primary)", borderRadius: "var(--radius-xl)", overflow: "hidden" }}>
                <Box sx={{ background: "linear-gradient(135deg, var(--color-primary-lighter) 0%, transparent 100%)", px: "var(--spacing-lg)", py: "var(--spacing-lg)", borderBottom: "1px solid var(--color-border-primary)", display: "flex", flexDirection: "column", gap: "var(--spacing-xs)" }}>
                  <Typography variant="title">Gainers & Losers</Typography>
                  <Typography variant="paragraph" sx={{ color: "var(--color-text-secondary)" }}>
                    Stocks with the largest post- and pre-market gains and losses
                  </Typography>
                </Box>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" }, gap: { xs: 0, sm: "var(--spacing-lg)" }, p: { xs: "var(--spacing-md)", md: "var(--spacing-xl)" } }}>
                  <StockTable title="Most Active" subtitle="Chg" data={marketMovers.actives} isLoading={loadingMarketMovers} />
                  <StockTable title="Top Gainers" subtitle="Chg" data={marketMovers.gainers} isLoading={loadingMarketMovers} />
                  <StockTable title="Top Losers" subtitle="Chg" data={marketMovers.losers} isLoading={loadingMarketMovers} />
                </Box>
              </Box>
            </HoverCard>
          </Box>

          {/* RIGHT SIDEBAR */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: "var(--spacing-xl)" }}>

            {/* Customize Dashboard */}
            <HoverCard>
              <Box sx={{ backgroundColor: "var(--color-surface)", border: "1px solid var(--color-border-primary)", borderRadius: "var(--radius-xl)", p: "var(--spacing-xl)", display: "flex", flexDirection: "column", gap: "var(--spacing-lg)" }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: "var(--spacing-xs)" }}>
                  <Typography variant="title" sx={{ fontWeight: "var(--font-weight-semibold)", color: "var(--color-text-primary)" }}>
                    Customize Dashboard
                  </Typography>
                  <Typography variant="paragraph" sx={{ color: "var(--color-text-secondary)" }}>
                    Adjust topics and their order.
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => {
                    setMyFinlyticOpen(true);
                    setSaving(false);
                  }}
                  sx={{
                    fontSize: "var(--font-size-base)",
                    fontWeight: "var(--font-weight-semibold)",
                  }}
                >
                  My FactorAlpha
                </Button>

              </Box>
            </HoverCard>

            {/* Quick Access */}
            {visibleQuickAccessItems.length > 0 && (
              <HoverCard>
                <Box sx={{ backgroundColor: "var(--color-surface)", border: "1px solid var(--color-border-primary)", borderRadius: "var(--radius-xl)", p: "var(--spacing-xl)", display: "flex", flexDirection: "column", gap: "var(--spacing-lg)" }}>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: "var(--spacing-xs)" }}>
                    <Typography variant="title">Quick Access Menu</Typography>
                    <Typography variant="paragraph" sx={{ color: "var(--color-text-secondary)" }}>
                      Click a card to view associated data.
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: "var(--spacing-sm)" }}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: "var(--spacing-sm)" }}>
                      {visibleQuickAccessItems.map((item) => (
                        <Button
                          key={item.id}
                          variant="outlined"
                          fullWidth
                          disabled={!item.onClick}
                          onClick={item.onClick}
                          sx={{
                            justifyContent: "space-between",
                            fontSize: "var(--font-size-base)",
                            fontWeight: "var(--font-weight-semibold)",
                          }}
                        >
                          {item.label}
                          <ChevronRight size={18} />
                        </Button>
                      ))}
                    </Box>
                  </Box>
                </Box>
              </HoverCard>
            )}
          </Box>
        </Box>

        {/* ── Trending Stocks (full width, outside the 3fr/1fr grid) ── */}
        <Box sx={{ mt: "var(--spacing-xl)" }}>
          <HoverCard>
            <Box sx={{ backgroundColor: "var(--color-surface)", border: "1px solid var(--color-border-primary)", borderRadius: "var(--radius-xl)", overflow: "hidden" }}>
              <Box sx={{ background: "linear-gradient(135deg, var(--color-primary-lighter) 0%, transparent 100%)", px: "var(--spacing-lg)", py: "var(--spacing-lg)", borderBottom: "1px solid var(--color-border-primary)", display: "flex", flexDirection: "column", gap: "var(--spacing-xs)" }}>
                <Typography variant="title">Trending Stocks</Typography>
                <Typography variant="paragraph" sx={{ color: "var(--color-text-secondary)" }}>
                  The most searched stocks on FactorAlpha
                </Typography>
              </Box>
              <TrendingTable title="Trending" data={trendingStocks} />
            </Box>
          </HoverCard>
        </Box>
      </Box>

      {/* Dialogs */}
      <EarningsDialog open={earningsDialogOpen} onClose={() => setEarningsDialogOpen(false)} />
      <MyEditDialog open={myFinlyticOpen} onClose={() => setMyFinlyticOpen(false)} sections={quickAccessConfig} setSections={setQuickAccessConfig} saving={saving} setSaving={setSaving} />
      <TopStocksDialog open={topStocksOpen} onClose={() => setTopStocksOpen(false)} />
      <TopYieldingStocksDialog open={topYieldingOpen} onClose={() => setTopYieldingOpen(false)} />

      <style>{`
        .mini-stock-graph { display: block; }
        @media (max-width: 600px) {
          .mini-stock-graph { display: none; }
        }
        .ai-insight-description { padding-right: 140px; }
        @media (max-width: 600px) {
          .ai-insight-description { padding-right: 0; }
        }
        @media (hover: none) {
          .hover-card-transform { transform: none !important; }
        }
        .mini-stock-graph:hover { opacity: 0.9 !important; }
        .news-border {
          position: relative;
          border-radius: 13px;
          padding: 1px;
          background-size: 200% 100%;
          border: 1px solid var(--color-primary);
        }
        .news-inner {
          animation: swiftEnter 0.4s ease-out both;
        }
        @keyframes swiftEnter {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </Box>
  );
};

export default Dashboard;